(() => {
  if (customElements.get('product-description-expand')) return;

  const DURATION_MS = 450;

  class ProductDescriptionExpand extends HTMLElement {
    connectedCallback() {
      this._inner = this.querySelector('[data-ref="inner"]');
      this._toggle = this.querySelector('[data-ref="toggle"]');
      this._labelMore = this.querySelector('[data-ref="label-more"]');
      this._labelLess = this.querySelector('[data-ref="label-less"]');
      if (!this._inner || !this._toggle) return;

      const raw = getComputedStyle(this).getPropertyValue('--product-description-expand-max').trim();
      this._max = parseInt(raw, 10);
      if (!this._max || Number.isNaN(this._max)) this._max = 280;

      this._animating = false;
      this._onTransitionEnd = this._onTransitionEnd.bind(this);
      this._fallbackTimer = null;

      this._onResize = () => this._syncOverflow();
      window.addEventListener('resize', this._onResize);

      requestAnimationFrame(() => this._syncOverflow());
      this._toggle.addEventListener('click', () => this._toggleExpand());
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      this._clearTransitionListeners();
    }

    _clearTransitionListeners() {
      this._inner?.removeEventListener('transitionend', this._onTransitionEnd);
      if (this._fallbackTimer != null) {
        clearTimeout(this._fallbackTimer);
        this._fallbackTimer = null;
      }
    }

    _scheduleFallback(done) {
      if (this._fallbackTimer != null) clearTimeout(this._fallbackTimer);
      this._fallbackTimer = window.setTimeout(() => {
        this._fallbackTimer = null;
        this._inner?.removeEventListener('transitionend', this._onTransitionEnd);
        done();
      }, DURATION_MS + 80);
    }

    _onTransitionEnd(event) {
      if (event.target !== this._inner || event.propertyName !== 'max-height') return;
      if (this._fallbackTimer != null) {
        clearTimeout(this._fallbackTimer);
        this._fallbackTimer = null;
      }
      this._inner.removeEventListener('transitionend', this._onTransitionEnd);
      if (this._pendingExpandComplete) {
        this._pendingExpandComplete = false;
        this._inner.style.maxHeight = 'none';
        this._animating = false;
        return;
      }
      if (this._pendingCollapseComplete) {
        this._pendingCollapseComplete = false;
        this._animating = false;
        this._syncOverflow();
      }
    }

    _syncOverflow() {
      if (!this._toggle || !this._inner || this._animating) return;

      if (this.classList.contains('is-expanded')) {
        this._toggle.hidden = false;
        return;
      }

      this._inner.style.maxHeight = `${this._max}px`;
      const needsToggle = this._inner.scrollHeight > this._max + 2;
      this._toggle.hidden = !needsToggle;
      this.classList.toggle('has-overflow', needsToggle);
    }

    _toggleExpand() {
      if (!this._inner || !this._toggle || this._animating) return;

      const expand = !this.classList.contains('is-expanded');
      this.classList.toggle('is-expanded', expand);
      this._toggle.setAttribute('aria-expanded', expand ? 'true' : 'false');

      if (this._labelMore && this._labelLess) {
        this._labelMore.hidden = expand;
        this._labelLess.hidden = !expand;
      }

      this._clearTransitionListeners();
      this._pendingExpandComplete = false;
      this._pendingCollapseComplete = false;

      if (expand) {
        const fullH = this._inner.scrollHeight;
        if (fullH <= this._max + 2) {
          this._inner.style.maxHeight = 'none';
          this._animating = false;
          return;
        }

        this._animating = true;
        this._inner.style.maxHeight = `${this._max}px`;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this._pendingExpandComplete = true;
            this._inner.addEventListener('transitionend', this._onTransitionEnd);
            this._scheduleFallback(() => {
              this._pendingExpandComplete = false;
              this._inner.style.maxHeight = 'none';
              this._animating = false;
            });
            this._inner.style.maxHeight = `${fullH}px`;
          });
        });
      } else {
        this._animating = true;
        const startH = this._inner.scrollHeight;
        this._inner.style.maxHeight = `${startH}px`;
        void this._inner.offsetHeight;
        requestAnimationFrame(() => {
          this._pendingCollapseComplete = true;
          this._inner.addEventListener('transitionend', this._onTransitionEnd);
          this._scheduleFallback(() => {
            this._pendingCollapseComplete = false;
            this._animating = false;
            this._syncOverflow();
          });
          this._inner.style.maxHeight = `${this._max}px`;
        });
      }
    }
  }

  customElements.define('product-description-expand', ProductDescriptionExpand);
})();
